import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetDietDetailsQuery,
  useAssignDietApplicationsToAssessorMutation,
} from "../api/logbookDietApi";
import { useGetActiveApplicationCommitteeMembersQuery } from "../../committee/api/committeesApi";
import DataTable from "../components/DataTable";

/* ---------------- Utilities ---------------- */

function pct(part: number, total: number) {
  if (!total) return 0;
  return Math.round((part / total) * 100);
}

/* ---------------- Component ---------------- */

export default function DietApplicationAssessmentDetailsPage() {
  const navigate = useNavigate();
  const { id = "" } = useParams();

  const { data, isLoading, error, refetch } = useGetDietDetailsQuery(id, {
    skip: !id,
  });

  const { data: committeeRes } = useGetActiveApplicationCommitteeMembersQuery();

  const committeeMembers = committeeRes?.members || [];

  const diet = data?.diet;
  const applications = data?.applications || [];

  const unreviewedApplications = useMemo(() => {
    return applications.filter(
      (app: any) => app.reviewed_by !== null && app.status === "acknowledged"
    );
  }, [applications]);

  /* ================= KPIs ================= */

  const reviewedCount = applications.filter(
    (a: any) => a.reviewed_by === true
  ).length;

  const notReviewedCount = applications.filter(
    (a: any) => !a.reviewed_by
  ).length;

  const totalApplications = applications.length;

  const reviewedPct = pct(reviewedCount, totalApplications);
  const notReviewedPct = pct(notReviewedCount, totalApplications);

  const doughnutStyle = useMemo(() => {
    return {
      background: `conic-gradient(#111 ${reviewedPct}%, #e5e7eb 0)`,
    } as React.CSSProperties;
  }, [reviewedPct]);

  /* ================= Assign ================= */

  const [openAssign, setOpenAssign] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [pickedApplications, setPickedApplications] = useState<string[]>([]);
  const [assignErr, setAssignErr] = useState<string | null>(null);
  const [assignOk, setAssignOk] = useState<string | null>(null);

  const [assignApplications, { isLoading: assigning }] =
    useAssignDietApplicationsToAssessorMutation();

  const toggleApplication = (appId: string) => {
    setPickedApplications((prev) =>
      prev.includes(appId) ? prev.filter((x) => x !== appId) : [...prev, appId]
    );
  };

  const assignSelected = async () => {
    if (!selectedMemberId) {
      setAssignErr("Please select a committee member.");
      return;
    }

    if (!pickedApplications.length) {
      setAssignErr("Please select at least one application.");
      return;
    }

    try {
      const res = await assignApplications({
        dietId: id,
        assessor_member_id: selectedMemberId,
        logbook_ids: pickedApplications,
      }).unwrap();

      setAssignOk(res?.message || "Applications assigned successfully");
      setPickedApplications([]);
      await refetch();
    } catch (e: any) {
      setAssignErr(
        e?.data?.message || e?.message || "Failed to assign applications"
      );
    }
  };

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Failed to load diet.</div>;
  if (!diet) return <div>No data.</div>;

  return (
    <div className="bg-white border rounded-xl p-6 space-y-8">
      {/* ================= Header ================= */}
      <div className="flex justify-between items-start">
        <div>
          <button
            onClick={() => navigate("/logbook-diets")}
            className="text-sm text-gray-600 hover:underline"
          >
            ← Back
          </button>
          <h1 className="text-xl font-bold mt-2">{diet.title}</h1>
        </div>

        <button
          onClick={() => setOpenAssign(true)}
          className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm"
        >
          Assign Applications
        </button>
      </div>

      {/* ================= KPIs ================= */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="border rounded-xl p-4">
          <div className="text-xs text-gray-500">Total Applications</div>
          <div className="text-2xl font-bold mt-1">{totalApplications}</div>
        </div>

        <div className="border rounded-xl p-4">
          <div className="text-xs text-gray-500">Reviewed</div>
          <div className="text-2xl font-bold mt-1">{reviewedCount}</div>
          <div className="text-sm text-gray-500 mt-1">{reviewedPct}%</div>
        </div>

        <div className="border rounded-xl p-4">
          <div className="text-xs text-gray-500">Not Reviewed</div>
          <div className="text-2xl font-bold mt-1">{notReviewedCount}</div>
          <div className="text-sm text-gray-500 mt-1">{notReviewedPct}%</div>
        </div>
      </div>

      {/* Doughnut */}
      <div className="border rounded-xl p-4 flex items-center gap-4">
        <div className="w-16 h-16 rounded-full" style={doughnutStyle} />
        <div>
          <div className="font-semibold">Reviewed vs Not Reviewed</div>
          <div className="text-sm text-gray-500">
            Reviewed: {reviewedCount} • Not Reviewed: {notReviewedCount}
          </div>
        </div>
      </div>

      {/* ================= Committee Members Section ================= */}
      <div className="border rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-gray-800">Committee Members</div>
            <div className="text-sm text-gray-500">
              Click a member to view assigned applications.
            </div>
          </div>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2">Name</th>
                <th className="py-2">Role</th>
                <th className="py-2">Assigned</th>
                <th className="py-2">Reviewed</th>
                <th className="py-2">Pending</th>
                <th className="py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {committeeMembers.map((m: any) => {
                const member = m.member;

                return (
                  <tr key={member?.id} className="border-b">
                    <td className="py-3 font-semibold text-gray-800">
                      {member?.surname} {member?.other_names}
                    </td>

                    <td className="py-3">
                      <span
                        className={`px-2 py-1 text-xs rounded-full border font-medium ${
                          m.committee_role === "chairman"
                            ? "bg-green-100 text-green-700 border-green-200"
                            : m.committee_role === "secretary"
                            ? "bg-blue-100 text-blue-700 border-blue-200"
                            : "bg-gray-100 text-gray-700 border-gray-200"
                        }`}
                      >
                        {m.committee_role}
                      </span>
                    </td>

                    <td className="py-3">
                      {m.total_assigned_applications ?? 0}
                    </td>
                    <td className="py-3">
                      {m.total_reviewed_applications ?? 0}
                    </td>
                    <td className="py-3">
                      {m.total_pending_applications ?? 0}
                    </td>

                    <td className="py-3">
                      <button
                        className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-xs"
                        onClick={() =>
                          navigate(
                            `/logbook-diets/${id}/committee/${member?.id}/applications`
                          )
                        }
                      >
                        View applications
                      </button>
                    </td>
                  </tr>
                );
              })}

              {!committeeMembers.length && (
                <tr>
                  <td colSpan={6} className="py-6 text-gray-500">
                    No committee members available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================= Applications Table ================= */}
      <DataTable
        title="Applications"
        data={applications}
        searchKeys={["surname", "other_names", "email"]}
        columns={[
          {
            key: "name",
            header: "Name",
            accessor: (r: any) => `${r.surname ?? ""} ${r.other_names ?? ""}`,
          },
          { key: "email", header: "Email", accessor: (r: any) => r.email },
          {
            key: "reviewed",
            header: "Status",
            accessor: (r: any) => (r.reviewed ? "Reviewed" : "Not Reviewed"),
          },
        ]}
        emptyText="No applications found."
      />

      {/* ================= Assign Modal ================= */}
      {openAssign && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-4xl rounded-xl p-5">
            <h2 className="font-semibold text-lg">
              Assign Unreviewed Applications
            </h2>

            <div className="grid md:grid-cols-2 gap-4 mt-4">
              {/* Committee Members */}
              <div className="border rounded-xl p-4 max-h-[300px] overflow-y-auto">
                {committeeMembers.map((m: any) => {
                  const member = m.member;
                  const active = selectedMemberId === String(member?.id);

                  return (
                    <button
                      key={member?.id}
                      onClick={() => setSelectedMemberId(String(member?.id))}
                      className={`w-full text-left p-3 border rounded-lg mb-2 ${
                        active ? "bg-gray-100 border-gray-400" : ""
                      }`}
                    >
                      <div className="font-semibold">
                        {member?.surname} {member?.other_names}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Applications */}
              <div className="border rounded-xl p-4 max-h-[300px] overflow-y-auto">
                {isLoading ? (
                  <div>Loading...</div>
                ) : (
                  unreviewedApplications.map((app: any) => (
                    <label key={app.id} className="flex gap-2 mb-2">
                      <input
                        type="checkbox"
                        checked={pickedApplications.includes(app.id)}
                        onChange={() => toggleApplication(app.id)}
                      />
                      <span>
                        {app?.surname} {app?.other_names}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setOpenAssign(false)}
                className="px-4 py-2 border rounded-lg"
              >
                Cancel
              </button>

              <button
                disabled={
                  !selectedMemberId || !pickedApplications.length || assigning
                }
                onClick={assignSelected}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg"
              >
                {assigning ? "Assigning..." : "Assign"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
